<template>
	<div>
		<router-link class="back-link" :to="{ name: 'drip-campaigns-overview' }">
			<font-awesome-icon :icon="['fas', 'chevron-left']" />Back to Overview
		</router-link>
		<div v-if="!current.error && !current.loading && currentDripCampaign">
			<div class="drip-campaing-heading">
				<h1>{{ currentDripCampaign.title }}</h1>
				<NotificationBar
					v-if="!userHasBlinkRegistration || !companyHasBlinkRegistration"
					class="notification"
					:icon="['far', 'exclamation-circle']"
				>
					<div>
						Sorry, Drip Campaigns are currently only available if you are setup
						with Blink+. To get setup with Blink+, please contact your UWM
						account executive at 1-800-981-8898. To learn more about Blink+,
						please
						<a
							href="https://www.uwm.com/manage-your-pipeline/blink"
							target="_blank"
							>click here</a
						>.
					</div>
				</NotificationBar>
				<div v-if="currentDripCampaignActive" class="enabled-campaign-status">
					This campaign is enabled for all eligible leads
				</div>
			</div>
			<p class="drip-campaign-item-description">
				{{ currentDripCampaign.description }}
			</p>

			<Tabs v-model="tab" aria-label="Campaign Tabs">
				<Tab name="campaignSummary" label="Summary" />
				<Tab name="campaignContacts" label="Contacts" />
				<Tab name="campaignSettings" label="Settings" />
			</Tabs>
			<TabItems :tab="tab" :pad="false">
				<TabItem name="campaignSummary">
					<DripCampaignItemSummary :items="currentDripCampaign.items" />
				</TabItem>
				<TabItem name="campaignContacts">
					<DripCampaignItemContacts
						:dripCampaignId="currentDripCampaign.dripCampaignId"
					/>
				</TabItem>
				<TabItem name="campaignSettings">
					<DripCampaignItemSettings
						:dripCampaignId="currentDripCampaign.dripCampaignId"
					/>
				</TabItem>
			</TabItems>
		</div>
		<div class="spinner" v-if="!current.error && current.loading">
			<LoadingIcon />
		</div>
		<div class="error" v-if="current.error && !current.loading">
			<p>{{ error }}</p>
		</div>
	</div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import dripCampaignService from '@/services/toolbox/drip-campaign-service';

import { Tab, Tabs, TabItem, TabItems } from '@/components/global/Tabs';
import LoadingIcon from '@/components/global/LoadingIcon.vue';
import NotificationBar from '@/components/global/NotificationBar.vue';

import DripCampaignItemContacts from './DripCampaignItemContacts';
import DripCampaignItemSettings from './DripCampaignItemSettings';
import DripCampaignItemSummary from './DripCampaignItemSummary';

export default {
	name: 'DripCampaignItem',
	components: {
		Tab,
		Tabs,
		TabItem,
		TabItems,
		LoadingIcon,
		NotificationBar,
		DripCampaignItemContacts,
		DripCampaignItemSettings,
		DripCampaignItemSummary,
	},
	data() {
		return {
			tab: 'campaignSummary',
			currentPreviewUri: null,
			error: null,
		};
	},
	computed: {
		...mapState({
			companyHasBlinkRegistration: (state) =>
				state.companyProfile.blinkRegistrationInd,
			userHasBlinkRegistration: (state) => state.profile.blinkRegistrationInd,
			current: (state) => state.dripCampaigns.current,
		}),
		...mapGetters(['currentDripCampaign', 'currentDripCampaignActive']),
	},
	methods: {
		...mapActions(['resetCurrentDripCampaign']),
	},
	watch: {
		current(currentCampaign) {
			this.error = currentCampaign.error
				? `We were unable to get this Drip Campaign's information. Please try again in a few minutes.`
				: null;
		},
	},
	beforeDestroy() {
		this.resetCurrentDripCampaign();
	},
};
</script>

<style lang="scss" scoped>
.back-link {
	font-size: $b360-font-size--smaller;
	text-decoration: none;
	color: $brand-blue;

	[data-icon] {
		@include rem('margin-right', 3px);
	}

	&:hover {
		text-decoration: underline;
	}
}

.drip-campaign-item-description {
	max-width: 900px;
}

.drip-campaing-heading {
	display: flex;
	flex-direction: column;
	margin-bottom: 0;

	h1 {
		@include rem('margin-bottom', 12px);
	}

	.notification {
		align-items: flex-start;
		max-width: 960px;
		@include rem('margin-bottom', 12px);
		@include rem('padding', 6px);
		min-height: auto;

		/deep/ .icon {
			@include rem('margin', 1px 6px 0 0);
		}
	}

	.enabled-campaign-status {
		box-sizing: border-box;
		border-radius: 6px;
		width: fit-content;
		height: fit-content;
		border: 1px solid $brand-teal;
		@include rem('font-size', 13px);
		@include rem('padding', 4px 6px);
		line-height: 1;
		font-weight: bold;
		color: $brand-teal;
		background: rgba($brand-teal, 0.05);
	}
}

.error p {
	color: $error;
}
</style>
